import type { Notification } from "@loot-os/api-contract";
import { Button } from "@loot-os/ui/components/button";
import { NotificationItem } from "@loot-os/ui/components/notification-item";
import { ScrollArea } from "@loot-os/ui/components/scroll-area";
import { Skeleton } from "@loot-os/ui/components/skeleton";
import { groupNotificationsByDay } from "@loot-os/ui/lib/notifications";
import { useInfiniteQuery } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import { BellIcon } from "lucide-react";
import { type ReactNode, useId } from "react";

import {
  type NotificationFilter,
  notificationsInfiniteQueryOptions,
  notificationTarget,
  useNow,
} from "../../lib/notifications.js";
import { useShopper } from "../../lib/shopper.js";
import { useLoadMore } from "../../lib/useLoadMore.js";

/** The scroll area fills what the panel leaves, so the panel's max height bounds the list. */
const SCROLL_FILL =
  "flex min-h-0 flex-1 flex-col [&>[data-slot=scroll-area-viewport]]:min-h-0 [&>[data-slot=scroll-area-viewport]]:flex-1";

/** One tab's notifications under Today and Earlier, fetched as it shows and paged on scroll. */
export function NotificationList({
  filter,
  onRead,
  onNavigate,
}: {
  filter: NotificationFilter;
  onRead: (id: string) => void;
  onNavigate: () => void;
}) {
  const { client } = useShopper();
  const now = useNow();
  const query = useInfiniteQuery(notificationsInfiniteQueryOptions(client, filter));
  const notifications = query.data?.pages.flatMap((page) => page.data) ?? [];
  const sentinel = useLoadMore<HTMLDivElement>(
    !!query.hasNextPage && !query.isFetchingNextPage,
    () => void query.fetchNextPage(),
  );

  if (query.isPending) return <NotificationSkeletons />;
  if (query.isError) {
    return (
      <ListMessage
        title="Notifications did not load"
        body="Check your connection and try again."
        action={
          <Button variant="outline" size="sm" onClick={() => void query.refetch()}>
            Try again
          </Button>
        }
      />
    );
  }
  if (notifications.length === 0) {
    return filter === "unread" ? (
      <ListMessage title="You're all caught up" body="Nothing unread right now." />
    ) : (
      <ListMessage
        title="No notifications yet"
        body="Updates about your orders, balance and account show up here."
      />
    );
  }

  const { today, earlier } = groupNotificationsByDay(notifications, now);
  const rowProps = { now, onRead, onNavigate };
  return (
    <ScrollArea className={SCROLL_FILL}>
      <div className="pb-2">
        <NotificationGroup label="Today" notifications={today} {...rowProps} />
        <NotificationGroup label="Earlier" notifications={earlier} {...rowProps} />
        <div ref={sentinel} />
        {query.hasNextPage && (
          <div className="px-3 pt-1">
            <Button
              variant="ghost"
              size="sm"
              className="w-full"
              disabled={query.isFetchingNextPage}
              onClick={() => void query.fetchNextPage()}
            >
              {query.isFetchingNextPage ? "Loading…" : "Load more"}
            </Button>
          </div>
        )}
      </div>
    </ScrollArea>
  );
}

interface RowProps {
  now: Date;
  onRead: (id: string) => void;
  onNavigate: () => void;
}

function NotificationGroup({
  label,
  notifications,
  ...rowProps
}: RowProps & { label: string; notifications: Notification[] }) {
  const headingId = useId();
  if (notifications.length === 0) return null;
  return (
    <section aria-labelledby={headingId}>
      <h3
        id={headingId}
        className="px-4 pt-3 pb-1 text-xs font-medium tracking-wide text-muted-foreground uppercase"
      >
        {label}
      </h3>
      <ul className="flex flex-col gap-0.5 px-1.5">
        {notifications.map((notification) => (
          <li key={notification.id}>
            <NotificationRow notification={notification} {...rowProps} />
          </li>
        ))}
      </ul>
    </section>
  );
}

/**
 * One row, read on click: a storefront path follows through the router and closes the panel, an
 * https link opens in a new tab, and a row with neither is a button that only marks it read.
 */
function NotificationRow({
  notification,
  now,
  onRead,
  onNavigate,
}: RowProps & { notification: Notification }) {
  const read = () => {
    if (notification.readAt === null) onRead(notification.id);
  };
  const target = notificationTarget(notification);
  const render =
    target?.kind === "path" ? (
      <Link
        to={target.pathname}
        search={target.search}
        hash={target.hash || undefined}
        onClick={() => {
          read();
          onNavigate();
        }}
      />
    ) : target?.kind === "external" ? (
      // biome-ignore lint/a11y/useAnchorContent: NotificationItem renders the content into it.
      <a href={target.href} target="_blank" rel="noopener noreferrer" onClick={read} />
    ) : (
      <button type="button" onClick={read} />
    );
  return <NotificationItem notification={notification} now={now} render={render} />;
}

function NotificationSkeletons() {
  return (
    <div aria-busy="true" className="flex flex-col gap-1 px-4 py-3">
      <span className="sr-only">Loading notifications</span>
      {[0, 1, 2].map((row) => (
        <div key={row} className="flex items-start gap-2.5 py-2.5">
          <Skeleton className="size-9 shrink-0 rounded-lg" />
          <div className="flex flex-1 flex-col gap-2 pt-0.5">
            <Skeleton className="h-3.5 w-2/3" />
            <Skeleton className="h-3 w-full" />
          </div>
          <Skeleton className="h-3 w-6" />
        </div>
      ))}
    </div>
  );
}

function ListMessage({ title, body, action }: { title: string; body: string; action?: ReactNode }) {
  return (
    <div className="flex flex-col items-center gap-2 px-6 py-10 text-center">
      <span className="flex size-10 items-center justify-center rounded-full bg-muted text-muted-foreground">
        <BellIcon className="size-5" aria-hidden />
      </span>
      <p className="font-medium">{title}</p>
      <p className="text-sm text-muted-foreground">{body}</p>
      {action}
    </div>
  );
}
